import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { setDateLocaleCache } from '@/lib/dates';
import { DEFAULT_DATE_LOCALE } from '@/lib/companySettings';
import { loadEffectivePermissions, UNRESTRICTED_PERMISSIONS, type EffectivePermissions } from '@/lib/permissions';

// This DMS instance is single-tenant -- same fixed-tenant convention used
// throughout the codebase (incidentReports.ts, taxicaller_full_webhook, etc.).
const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

interface Job {
  id: string;
  pickupLocation: string;
  dropoffLocation: string;
  totalFare: number;
  paymentMethods: Array<{
    type: string;
    amount: number;
    accountNumber?: string;
    customType?: string;
  }>;
  hoistUsed: boolean;
  hoistCount: number;
  hoistFee: number;
  timestamp: Date;
  validated?: boolean;
}

interface Driver {
  id: string;
  driver_name: string;
  driver_firstname?: string;
  driver_lastname?: string;
  email?: string;
  phone?: string;
  password: string;
  rideWiseId?: string;
  carLogin?: string;
  vehicleId?: string;
  isAdmin: boolean;
  include_in_reports?: boolean;
  bank_account_number?: string;
  bank_account_name?: string;
  payout_reference_override?: string;
  insurance_start_date?: string;
  insurance_last_claim_date?: string;
  insurance_claim_free?: boolean;
  last_vehicle_inspection_at?: string;
  last_vehicle_inspection_shift_date?: string;
  vehicle_inspection_shift_counter?: number;
  payment_type?: string;
  is_active?: boolean;
  exempt_from_inspection?: boolean;
  roster_groups?: string[];
  created_at?: string;
  updated_at?: string;
  /** Set for every logged-in user (driver or portal user) -- the
   *  tenant_users.id row backing their session, used to resolve
   *  permission-group access. See src/lib/permissions.ts. */
  tenantUserId?: string;
  /** The real Supabase auth.users id behind this session at login time.
   *  Supabase persists its session to localStorage, which is shared by
   *  every tab on the origin -- logging into a second account in one tab
   *  (e.g. to test a portal/partner login) silently replaces the session
   *  every other open tab is using too. Caching this id lets the mount
   *  check below notice that mismatch and refuse to trust a stale
   *  `currentUser` that no longer matches the live session, instead of
   *  quietly logging someone back in as whoever's session happens to be
   *  live. See the confirmed 2026-10-01 bug: a refresh logged Janelle in
   *  as a partner/portal test account instead of her own admin login. */
  authUserId?: string;
  /** True for a non-driver "portal user" login (e.g. a lease operator's
   *  family member helping with bookwork) -- a tenant_users row with
   *  driver_name IS NULL, represented here as a Driver-shaped object so
   *  the rest of the app (which assumes currentUser is a Driver) doesn't
   *  need its own separate code path for them. */
  isPortalUser?: boolean;
}

interface FleetVehicle {
  id: string;
  fleetNumber: string;
  registration: string;
  model?: string;
  year?: string;
  eftId?: string;
  lastKm?: number;
  lastDriver?: string;
  lastUpdated?: Date;
}

interface BonusTier {
  min: number;
  max: number;
  bonus: number;
}

interface BreakEntry {
  id: string;
  startTime: Date;
  endTime?: Date;
  duration?: number;
}

interface TimesheetEntry {
  id: string;
  driverId: string;
  date: Date;
  loginTime: string;
  logoffTime: string;
  totalHours: number;
  breaks: number;
  breakEntries?: BreakEntry[];
  startKM: number;
  finishKM: number;
  totalKM: number;
  totalEarnings: number;
  vehicleId?: string;
}

interface PaymentType {
  id: string;
  name: string;
  hasMaximum: boolean;
  maximumAmount?: number;
  // Base / company-default commission (driver-payable) % for this payment type.
  // Standard (waged/commission employee) rate.
  baseCommissionPercent?: number | null;
  // Different base rates for contractors and leased vehicles.
  contractorPercent?: number | null;
  leasedPercent?: number | null;
  // Whether this payment type participates in driver commission calcs at all.
  appliesToDriverCommission?: boolean | null;
  // Per-unit (e.g. per-hoist) charging: the amount is entered as a count that
  // is multiplied by a fixed increment, rather than a free-form dollar value.
  isPerUnit?: boolean | null;
  perUnitAmount?: number | null;
}

interface AdminSettings {
  insuranceExcess: number;
  commissionPercent: number;
  gstPercent: number;
  bonusTiers: BonusTier[];
  tmHoist?: number;
}

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedDriver: Driver | null;
  originalAdmin: Driver | null;
  originalAdminRole: 'admin' | 'driver' | null;
}

interface AppContextType {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  jobs: Job[];
  addJob: (job: Omit<Job, 'id' | 'timestamp'>) => void;
  updateJob: (id: string, updates: Partial<Job>) => void;
  hoistFeeRate: number;
  setHoistFeeRate: (rate: number) => void;
  drivers: Driver[];
  setDrivers: (drivers: Driver[]) => void;
  updateDriver: (id: string, updates: Partial<Driver>) => void;
  fleetVehicles: FleetVehicle[];
  setFleetVehicles: (vehicles: FleetVehicle[]) => void;
  updateVehicleInfo: (vehicleId: string, driverName: string, finishKm: number, date: Date) => void;
  adminSettings: AdminSettings;
  updateAdminSettings: (settings: Partial<AdminSettings>) => void;
  calculateBonus: (totalEarnings: number) => number;
  timesheets: TimesheetEntry[];
  addTimesheet: (timesheet: Omit<TimesheetEntry, 'id'>) => void;
  updateTimesheet: (id: string, updates: Partial<TimesheetEntry>) => void;
  currentUser: Driver | null;
  login: (tenantId: string, name: string, password: string) => Promise<boolean>;
  loginPortalUser: (tenantId: string, email: string, password: string) => Promise<{ name: string } | null>;
  /** True once a real Supabase Auth session has been confirmed to belong
   *  to a row in `platform_admins` -- a superadmin isn't a member of any
   *  tenant (no `currentUser` Driver record), so this is deliberately its
   *  own separate flag rather than folded into currentUser/role. See
   *  AppLayout.tsx for how it routes to SuperadminDashboard instead of
   *  the normal Driver/Admin shells. */
  isSuperadmin: boolean;
  loginSuperadmin: (email: string, password: string) => Promise<boolean>;
  effectivePermissions: EffectivePermissions;
  effectivePermissionsResolved: boolean;
  logout: () => void;
  paymentTypes: PaymentType[];
  addPaymentType: (type: Omit<PaymentType, 'id'>) => Promise<void>;
  removePaymentType: (id: string) => Promise<void>;
  updatePaymentType: (id: string, updates: Partial<PaymentType>) => Promise<void>;
  mergePaymentTypes: (sourceId: string, targetId: string) => Promise<void>;
  loadDriversFromDB: () => Promise<Driver[]>;
  loadVehiclesFromDB: () => Promise<void>;
  loadPaymentTypes: () => Promise<void>;
  loadSettings: () => Promise<void>;
  impersonation: ImpersonationState;
  startImpersonation: (driver: Driver) => void;
  stopImpersonation: () => void;
}

const AppContext = createContext<AppContextType>({} as AppContextType);
export const useAppContext = () => useContext(AppContext);

const parseSettingNumber = (
  value: unknown,
  fallback: number
): number => {
  if (value === null || value === undefined || value === '') return fallback;

  const parsed =
    typeof value === 'number' ? value : parseFloat(String(value));

  return Number.isFinite(parsed) ? parsed : fallback;
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [hoistFeeRate, setHoistFeeRate] = useState(11.5);
  const [currentUser, setCurrentUser] = useState<Driver | null>(null);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [effectivePermissions, setEffectivePermissions] = useState<EffectivePermissions>(UNRESTRICTED_PERMISSIONS);
  // False for the brief window between currentUser being set and its
  // effectivePermissions actually resolving -- AppLayout uses this to hold
  // off rendering any dashboard content until a restricted user's real
  // scope is known, so they never see a flash of full/unscoped data.
  const [effectivePermissionsResolved, setEffectivePermissionsResolved] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);

  const [adminSettings, setAdminSettings] = useState<AdminSettings>({
    insuranceExcess: 15,
    commissionPercent: 60,
    gstPercent: 15,
    bonusTiers: [],
    tmHoist: 11.5,
  });

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [fleetVehicles, setFleetVehicles] = useState<FleetVehicle[]>([]);
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([]);

  const [impersonation, setImpersonation] = useState<ImpersonationState>({
    isImpersonating: false,
    impersonatedDriver: null,
    originalAdmin: null,
    originalAdminRole: null,
  });

  const loadSettings = async () => {
    const { data: settingsData, error: settingsError } = await supabase
      .from('settings')
      .select('*')
      .eq('is_active', true);

    const { data: bonusData, error: bonusError } = await supabase
      .from('bonustiers')
      .select('*')
      .order('min');

    if (settingsError) {
      console.error('Error loading settings:', settingsError);
      toast({
        title: 'Settings error',
        description: 'Could not load app settings. Fallback defaults are being used.',
        variant: 'destructive',
      });
    }

    if (bonusError) {
      console.error('Error loading bonus tiers:', bonusError);
    }

    const settings: Record<string, string> = {};

    settingsData?.forEach((setting) => {
      settings[setting.name] = setting.value;
    });

    const insuranceExcess = parseSettingNumber(settings.insurance_excess, 15);
    const commissionPercent = parseSettingNumber(settings.commission_percent, 60);
    const gstPercent = parseSettingNumber(settings.gst_percent, 15);
    const tmHoist = parseSettingNumber(settings.tm_hoist, 11.5);

    setAdminSettings({
      insuranceExcess,
      commissionPercent,
      gstPercent,
      tmHoist,
      bonusTiers:
        bonusData?.map((tier) => ({
          min: parseSettingNumber(tier.min, 0),
          max: parseSettingNumber(tier.max, 0),
          bonus: parseSettingNumber(tier.bonus, 0),
        })) || [],
    });

    setHoistFeeRate(tmHoist);

    // Populate the app-wide date/time formatting cache (src/lib/dates.ts) so
    // every screen -- payslips, reports, fleet capacity, driver portal --
    // shows dates in the configured time zone + format from the moment the
    // app loads, without each screen fetching this itself.
    setDateLocaleCache({
      timezone: settings['company.timezone'] || DEFAULT_DATE_LOCALE.timezone,
      dateFormat: settings['company.date_format'] || DEFAULT_DATE_LOCALE.dateFormat,
    });
  };

  const loadDriversFromDB = async () => {
    const { data, error } = await supabase
      .from('drivers')
      .select('*')
      .eq('is_active', true)
      .order('driver_firstname', { ascending: true });

    if (error) {
      console.error('Error loading drivers:', error);
      return [];
    }

    const mappedDrivers = (data || []).map((driver) => {
      const adminField = driver.AdminRights ?? driver.adminrights ?? driver.is_admin;
      const isAdmin =
        adminField === true ||
        (typeof adminField === 'string' && adminField.toLowerCase() === 'yes');

      const mapped: Driver = {
        id: (driver.id || 'unknown').toString(),
        driver_name: driver.driver_name || driver.name || 'Unknown Driver',
        driver_firstname: driver.driver_firstname || '',
        driver_lastname: driver.driver_lastname || '',
        email: driver.email || '',
        phone: driver.phone || '',
        password: driver.Password || driver.password || '1234',
        rideWiseId: driver.ridewise_id || '',
        carLogin: driver.car_login || driver.vehicle_number || '',
        vehicleId: (driver.vehicle_id || driver.vehicle_number || '').toString(),
        isAdmin,
        include_in_reports: driver.include_in_reports ?? true,
        bank_account_number: driver.bank_account_number || '',
        bank_account_name: driver.bank_account_name || '',
        payout_reference_override: driver.payout_reference_override || '',
        insurance_start_date: driver.insurance_start_date || '',
        insurance_last_claim_date: driver.insurance_last_claim_date || '',
        insurance_claim_free: driver.insurance_claim_free ?? false,
        last_vehicle_inspection_at: driver.last_vehicle_inspection_at || '',
        last_vehicle_inspection_shift_date: driver.last_vehicle_inspection_shift_date || '',
        vehicle_inspection_shift_counter: driver.vehicle_inspection_shift_counter || 0,
        payment_type: driver.payment_type || '',
        is_active: driver.is_active ?? true,
        exempt_from_inspection: driver.exempt_from_inspection ?? false,
        roster_groups: driver.roster_groups ?? [],
        created_at: driver.created_at || '',
        updated_at: driver.updated_at || '',
      };

      console.log(`🔍 Loaded driver: ${mapped.driver_name}, Admin: ${mapped.isAdmin}`);
      return mapped;
    });

    setDrivers(mappedDrivers);
    return mappedDrivers;
  };

  const loadVehiclesFromDB = async () => {
    const { data, error } = await supabase.from('vehicles').select('*');

    if (error) {
      console.error('Error loading vehicles:', error);
      return;
    }

    setFleetVehicles(
      (data || []).map((vehicle) => ({
        id: vehicle.vehicle_number || 'unknown',
        fleetNumber: vehicle.vehicle_number || 'unknown',
        registration: vehicle.registration || 'No Rego',
        model: vehicle.model || '',
        year: vehicle.year?.toString() || '',
        eftId: vehicle.eft_id || '',
      }))
    );
  };

  const loadPaymentTypes = async () => {
    const { data, error } = await supabase
      .from('payment_types')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error loading payment types:', error);
      return;
    }

    const rows = data || [];

    // Ensure the core payment types that the driver Payment Modal has fixed
    // fields for ("TM Hoist", "Other") always exist as CRUD-able records, so
    // their commission % can be configured in Settings and is wired through to
    // every payout report. (Previously "Other" was a modal-only field with no
    // matching payment type, so its commission could not be set.)
    const CORE_SEED_TYPES = ['TM Hoist', 'Other'];
    for (const seedName of CORE_SEED_TYPES) {
      const exists = rows.some(
        (r) => (r.name || '').toString().trim().toLowerCase() === seedName.toLowerCase()
      );
      if (!exists) {
        const { data: inserted, error: insertErr } = await supabase
          .from('payment_types')
          .insert({ name: seedName, has_maximum: false })
          .select();
        if (!insertErr && inserted && inserted.length > 0) {
          rows.push(inserted[0]);
        }
      }
    }

    const mapped = rows.map((paymentType) => ({
      id: paymentType.id.toString(),
      name: paymentType.name,
      hasMaximum: paymentType.has_maximum || false,
      maximumAmount: paymentType.maximum_amount ?? undefined,
      baseCommissionPercent:
        paymentType.base_commission_percent ??
        paymentType.driver_payable_percent ??
        null,
      contractorPercent: paymentType.contractor_percent ?? null,
      leasedPercent: paymentType.leased_percent ?? null,
      appliesToDriverCommission: paymentType.applies_to_driver_commission ?? null,
      isPerUnit: paymentType.is_per_unit ?? false,
      perUnitAmount:
        paymentType.per_unit_amount === null ||
        paymentType.per_unit_amount === undefined
          ? null
          : Number(paymentType.per_unit_amount),
    }));

    mapped.sort((a, b) => a.name.localeCompare(b.name));
    setPaymentTypes(mapped);
  };

  useEffect(() => {
    loadSettings();
    loadDriversFromDB();
    loadVehiclesFromDB();
    loadPaymentTypes();

    // Only trust a cached profile if a real Supabase session backs it --
    // otherwise anyone editing localStorage could forge a logged-in state.
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const storedUser = localStorage.getItem('currentUser');
      if (session && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        // Supabase's session lives in localStorage, shared by every tab on
        // this origin -- logging into a different account in one tab (e.g.
        // testing a portal/partner login) silently swaps the session every
        // other open tab is using. Without this check, a refresh on an
        // unrelated tab would happily log that tab's owner in as whoever's
        // session is now live. authUserId is missing on a profile cached
        // before this check existed -- treat that the same as a mismatch
        // rather than trusting it, since there's no way to confirm it's
        // still the right person.
        if (parsedUser.authUserId !== session.user.id) {
          localStorage.removeItem('currentUser');
          return;
        }
        // A driver switched to "Can log in = off" keeps a valid auth token,
        // but tenant isolation resolves their tenant through an active
        // tenant_users row, so they can no longer see any drivers. Only an
        // error-free empty result counts -- a network blip must not log
        // anyone out.
        const { data: visibleDrivers, error: visibleError } = await supabase.from('drivers').select('driver_name').limit(1);
        if (!visibleError && (!visibleDrivers || visibleDrivers.length === 0)) {
          await supabase.auth.signOut({ scope: 'local' });
          localStorage.removeItem('currentUser');
          localStorage.removeItem('isSuperadmin');
          return;
        }
        setCurrentUser(parsedUser);
      } else if (storedUser) {
        localStorage.removeItem('currentUser');
      }

      // Same defensive re-check as above, for the superadmin flag -- a
      // stale/forged localStorage value is never trusted on its own, only
      // ever a hint to re-verify against the live session + a real
      // is_platform_superadmin() check (RLS-backed, not just a client
      // flag someone could edit).
      const storedSuperadmin = localStorage.getItem('isSuperadmin');
      if (session && storedSuperadmin) {
        const { data: isSuper } = await supabase.rpc('is_platform_superadmin');
        if (isSuper) {
          setIsSuperadmin(true);
        } else {
          localStorage.removeItem('isSuperadmin');
        }
      } else if (storedSuperadmin) {
        localStorage.removeItem('isSuperadmin');
      }
    })();

    const storedImpersonation = sessionStorage.getItem('impersonationState');
    if (storedImpersonation) {
      setImpersonation(JSON.parse(storedImpersonation));
    }
  }, []);

  // Resolves whether the current user is permission-group-restricted
  // whenever they change (login, logout, impersonation). A portal user
  // already carries tenantUserId from loginPortalUser; a driver's isn't on
  // the Driver object itself, so it's looked up once here -- this app is
  // single-tenant today, same DEFAULT_TENANT_ID convention used throughout
  // (incidentReports.ts, edge functions, etc.).
  useEffect(() => {
    let cancelled = false;
    setEffectivePermissionsResolved(false);

    const resolve = async () => {
      if (!currentUser) {
        setEffectivePermissions(UNRESTRICTED_PERMISSIONS);
        setEffectivePermissionsResolved(true);
        return;
      }

      let tenantUserId = currentUser.tenantUserId ?? null;
      if (!tenantUserId) {
        const { data } = await supabase
          .from('tenant_users')
          .select('id')
          .eq('tenant_id', DEFAULT_TENANT_ID)
          .eq('driver_name', currentUser.driver_name)
          .eq('is_active', true)
          .maybeSingle();
        tenantUserId = data?.id ?? null;
      }

      const permissions = await loadEffectivePermissions(tenantUserId);
      if (!cancelled) {
        setEffectivePermissions(permissions);
        setEffectivePermissionsResolved(true);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const login = async (tenantId: string, name: string, password: string): Promise<boolean> => {
    const { data, error } = await supabase.functions.invoke('driver-login', {
      body: { tenant_id: tenantId, driver_name: name, password },
    });

    if (error || !data?.access_token || !data?.refresh_token) {
      return false;
    }

    const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    if (sessionError) return false;

    // drivers is RLS-locked (bank details, insurance history, etc.) --
    // the initial load on mount always ran as anon, before any session
    // existed, so on a fresh browser/cleared cache with no persisted
    // session it comes back empty and never gets a reason to refresh
    // itself afterward. Searching that stale closure here would fail a
    // login that the driver-login Edge Function above already verified
    // was correct. Re-fetch now that setSession has established a real
    // session, and search the freshly-returned array instead of
    // whatever `drivers` happened to be when this function was defined.
    const freshDrivers = await loadDriversFromDB();
    const driver = freshDrivers.find((d) => d.driver_name === name);
    if (!driver) return false;

    // Tag with the real auth user id so a stale cache from a different
    // account logged into elsewhere on this browser is never trusted --
    // see the mount check above.
    driver.authUserId = sessionData.session?.user.id;

    // Never persist the plaintext password field into localStorage.
    localStorage.setItem('currentUser', JSON.stringify({ ...driver, password: undefined }));
    setCurrentUser(driver);
    return true;
  };

  /** Logs in a non-driver "portal user" (e.g. a lease operator's family
   *  member helping with bookwork) -- same real-session shape as login()
   *  above, via the portal-login edge function instead of driver-login.
   *  There's no drivers-table row to look up here, so the Driver-shaped
   *  currentUser is built directly from what portal-login returns. */
  const loginPortalUser = async (tenantId: string, email: string, password: string): Promise<{ name: string } | null> => {
    const { data, error } = await supabase.functions.invoke('portal-login', {
      body: { tenant_id: tenantId, email, password },
    });

    if (error || !data?.access_token || !data?.refresh_token || !data?.portal_user) {
      return null;
    }

    const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    if (sessionError) return null;

    const portalDriver: Driver = {
      id: data.portal_user.tenant_user_id,
      driver_name: data.portal_user.display_name,
      email: data.portal_user.email,
      password: '',
      isAdmin: false,
      isPortalUser: true,
      tenantUserId: data.portal_user.tenant_user_id,
      // Tag with the real auth user id so a stale cache from a different
      // account logged into elsewhere on this browser is never trusted --
      // see the mount check above.
      authUserId: sessionData.session?.user.id,
    };

    localStorage.setItem('currentUser', JSON.stringify(portalDriver));
    setCurrentUser(portalDriver);
    return { name: portalDriver.driver_name };
  };

  /** Superadmin login -- deliberately NOT the driver-login/portal-login
   *  bridge those two use (they exist because drivers don't have real
   *  emails/traditional accounts). A superadmin has a normal email, so
   *  this is just plain Supabase Auth, then a real server-side check
   *  (is_platform_superadmin(), RLS-backed via platform_admins) before
   *  trusting it -- a valid Supabase login alone proves nothing about
   *  superadmin status. Deliberately vague on failure (no distinction
   *  between "wrong password" and "not a superadmin") and signs out
   *  immediately rather than leaving a non-superadmin session sitting
   *  active from this path.
   */
  const loginSuperadmin = async (email: string, password: string): Promise<boolean> => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.session) return false;

    const { data: isSuper, error: checkError } = await supabase.rpc('is_platform_superadmin');
    if (checkError || !isSuper) {
      await supabase.auth.signOut();
      return false;
    }

    localStorage.setItem('isSuperadmin', 'true');
    setIsSuperadmin(true);
    return true;
  };

  const logout = () => {
    if (impersonation.isImpersonating) {
      stopImpersonation();
      return;
    }

    supabase.auth.signOut();
    setCurrentUser(null);
    setIsSuperadmin(false);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('isSuperadmin');
  };

  const startImpersonation = (driver: Driver) => {
    if (!currentUser?.isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can use the impersonation feature.',
        variant: 'destructive',
      });
      return;
    }

    const newImpersonationState: ImpersonationState = {
      isImpersonating: true,
      impersonatedDriver: driver,
      originalAdmin: currentUser,
      originalAdminRole: 'admin',
    };

    setImpersonation(newImpersonationState);
    sessionStorage.setItem('impersonationState', JSON.stringify(newImpersonationState));

    toast({
      title: 'Impersonation Started',
      description: `Now viewing as ${driver.driver_name}. Click "Stop Impersonating" to return to admin view.`,
    });
  };

  const stopImpersonation = () => {
    if (!impersonation.isImpersonating) return;

    const adminName = impersonation.originalAdmin?.driver_name || 'Admin';

    setImpersonation({
      isImpersonating: false,
      impersonatedDriver: null,
      originalAdmin: null,
      originalAdminRole: null,
    });

    sessionStorage.removeItem('impersonationState');

    toast({
      title: 'Impersonation Ended',
      description: `Returned to admin view as ${adminName}.`,
    });
  };

  const addJob = (jobData: Omit<Job, 'id' | 'timestamp'>) => {
    const newJob: Job = {
      ...jobData,
      id: uuidv4(),
      timestamp: new Date(),
    };

    setJobs((prev) => [...prev, newJob]);
  };

  const updateJob = (id: string, updates: Partial<Job>) => {
    setJobs((prev) =>
      prev.map((job) => (job.id === id ? { ...job, ...updates } : job))
    );
  };

  const updateDriver = (id: string, updates: Partial<Driver>) => {
    setDrivers((prev) =>
      prev.map((driver) => (driver.id === id ? { ...driver, ...updates } : driver))
    );
  };

  const updateVehicleInfo = (
    vehicleId: string,
    driverName: string,
    finishKm: number,
    date: Date
  ) => {
    setFleetVehicles((prev) =>
      prev.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              lastKm: finishKm,
              lastDriver: driverName,
              lastUpdated: date,
            }
          : vehicle
      )
    );
  };

  const updateAdminSettings = (updates: Partial<AdminSettings>) => {
    setAdminSettings((prev) => ({ ...prev, ...updates }));
  };

  const calculateBonus = (total: number): number => {
    const tiers = Array.isArray(adminSettings.bonusTiers)
      ? adminSettings.bonusTiers
      : [];

    const tier = tiers.find((item) => total >= item.min && total < item.max);
    return tier ? tier.bonus : 0;
  };

  const addTimesheet = (data: Omit<TimesheetEntry, 'id'>) => {
    const newTimesheet: TimesheetEntry = {
      ...data,
      id: uuidv4(),
    };

    setTimesheets((prev) => [...prev, newTimesheet]);
  };

  const updateTimesheet = (id: string, updates: Partial<TimesheetEntry>) => {
    setTimesheets((prev) =>
      prev.map((timesheet) =>
        timesheet.id === id ? { ...timesheet, ...updates } : timesheet
      )
    );
  };

  // Translate the in-app PaymentType shape to DB column names.
  const toDbRow = (
    t: Partial<PaymentType>,
    extended: boolean
  ): Record<string, unknown> => {
    const base: Record<string, unknown> = {};
    if (t.name !== undefined) base.name = t.name;
    if (t.hasMaximum !== undefined) base.has_maximum = t.hasMaximum;
    if (t.maximumAmount !== undefined)
      base.maximum_amount = t.hasMaximum ? t.maximumAmount ?? null : null;

    if (extended) {
      if (t.baseCommissionPercent !== undefined)
        base.base_commission_percent = t.baseCommissionPercent;
      if (t.contractorPercent !== undefined)
        base.contractor_percent = t.contractorPercent;
      if (t.leasedPercent !== undefined)
        base.leased_percent = t.leasedPercent;
      if (t.appliesToDriverCommission !== undefined)
        base.applies_to_driver_commission = t.appliesToDriverCommission;
      if (t.isPerUnit !== undefined) base.is_per_unit = t.isPerUnit;
      if (t.perUnitAmount !== undefined)
        base.per_unit_amount = t.isPerUnit ? t.perUnitAmount ?? null : null;
    }
    return base;
  };

  // True when an error looks like an "unknown column" schema error so we can
  // gracefully retry with just the core columns.
  const isColumnError = (error: { message?: string } | null): boolean => {
    const msg = (error?.message || '').toLowerCase();
    return (
      msg.includes('column') ||
      msg.includes('schema cache') ||
      msg.includes('does not exist')
    );
  };

  const addPaymentType = async (type: Omit<PaymentType, 'id'>) => {
    let { error } = await supabase
      .from('payment_types')
      .insert(toDbRow(type, true));

    if (error && isColumnError(error)) {
      ({ error } = await supabase
        .from('payment_types')
        .insert(toDbRow(type, false)));
    }

    if (error) {
      console.error('Error adding payment type:', error);
      toast({
        title: 'Save failed',
        description: error.message || 'Could not add payment type.',
        variant: 'destructive',
      });
      throw error;
    }

    await loadPaymentTypes();
  };

  const removePaymentType = async (id: string) => {
    const { error } = await supabase
      .from('payment_types')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error removing payment type:', error);
      toast({
        title: 'Delete failed',
        description: error.message || 'Could not remove payment type.',
        variant: 'destructive',
      });
      throw error;
    }

    await loadPaymentTypes();
  };

  // Detect a Postgres unique-violation (duplicate name) regardless of how the
  // error object is shaped by the client.
  const isDuplicateNameError = (
    error: { code?: string; message?: string } | null
  ): boolean => {
    if (!error) return false;
    if (error.code === '23505') return true;
    const msg = (error.message || '').toLowerCase();
    return (
      msg.includes('duplicate key') ||
      msg.includes('payment_types_name_key') ||
      msg.includes('already exists')
    );
  };

  const updatePaymentType = async (id: string, updates: Partial<PaymentType>) => {
    // Block a rename that collides with another existing payment type *before*
    // it hits the DB unique constraint. This lets the UI offer to merge the
    // two so the modal and payout reports stay perfectly aligned.
    if (typeof updates.name === 'string') {
      const targetName = updates.name.trim().toLowerCase();
      const clash = paymentTypes.find(
        (t) => t.id !== id && t.name.trim().toLowerCase() === targetName
      );
      if (clash) {
        const dupErr = Object.assign(
          new Error(
            `A payment type named "${clash.name}" already exists. Merge these two so they stay consistent across the driver modal and payout reports.`
          ),
          { code: 'DUPLICATE_NAME', existingId: clash.id }
        );
        throw dupErr;
      }
    }

    let { error } = await supabase
      .from('payment_types')
      .update(toDbRow(updates, true))
      .eq('id', id);

    if (error && isColumnError(error)) {
      ({ error } = await supabase
        .from('payment_types')
        .update(toDbRow(updates, false))
        .eq('id', id));
    }

    if (error) {
      console.error('Error updating payment type:', error);

      if (isDuplicateNameError(error)) {
        const targetName = (updates.name || '').trim().toLowerCase();
        const clash = paymentTypes.find(
          (t) => t.id !== id && t.name.trim().toLowerCase() === targetName
        );
        const dupErr = Object.assign(
          new Error(
            `A payment type with that name already exists. Merge these two so they stay consistent across the driver modal and payout reports.`
          ),
          { code: 'DUPLICATE_NAME', existingId: clash?.id }
        );
        throw dupErr;
      }

      toast({
        title: 'Update failed',
        description: error.message || 'Could not update payment type.',
        variant: 'destructive',
      });
      throw error;
    }

    await loadPaymentTypes();
  };

  // Merge a duplicate (source) payment type into the canonical (target) one.
  // Any per-driver commission overrides that point at the source are moved to
  // the target (without clobbering an existing target override), and the source
  // record is then deleted. This keeps a single canonical payment type so the
  // driver Payment Modal and the payout reports always reference the same name.
  const mergePaymentTypes = async (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;

    try {
      // Re-point any driver overrides from source -> target where the driver
      // does not already have a target override.
      const { data: srcOverrides } = await supabase
        .from('driver_payment_type_percentages')
        .select('*')
        .eq('payment_type_id', sourceId);

      const { data: tgtOverrides } = await supabase
        .from('driver_payment_type_percentages')
        .select('driver_id')
        .eq('payment_type_id', targetId);

      const driversWithTarget = new Set(
        (tgtOverrides || []).map((r) => String(r.driver_id))
      );

      for (const ov of srcOverrides || []) {
        if (driversWithTarget.has(String(ov.driver_id))) {
          // Target override already exists for this driver — drop the source one.
          await supabase
            .from('driver_payment_type_percentages')
            .delete()
            .eq('id', ov.id);
        } else {
          await supabase
            .from('driver_payment_type_percentages')
            .update({ payment_type_id: targetId })
            .eq('id', ov.id);
        }
      }
    } catch (e) {
      // The overrides table may not exist in every environment — merging the
      // payment type itself is still the important part, so continue.
      console.warn('Could not migrate driver overrides during merge:', e);
    }

    const { error } = await supabase
      .from('payment_types')
      .delete()
      .eq('id', sourceId);

    if (error) {
      console.error('Error merging payment types:', error);
      toast({
        title: 'Merge failed',
        description: error.message || 'Could not merge payment types.',
        variant: 'destructive',
      });
      throw error;
    }

    await loadPaymentTypes();
  };

  return (
    <AppContext.Provider
      value={{
        sidebarOpen,
        toggleSidebar,
        jobs,
        addJob,
        updateJob,
        hoistFeeRate,
        setHoistFeeRate,
        drivers,
        setDrivers,
        updateDriver,
        fleetVehicles,
        setFleetVehicles,
        updateVehicleInfo,
        adminSettings,
        updateAdminSettings,
        calculateBonus,
        timesheets,
        addTimesheet,
        updateTimesheet,
        currentUser,
        login,
        loginPortalUser,
        isSuperadmin,
        loginSuperadmin,
        effectivePermissions,
        effectivePermissionsResolved,
        logout,
        paymentTypes,
        addPaymentType,
        removePaymentType,
        updatePaymentType,
        mergePaymentTypes,
        loadDriversFromDB,
        loadVehiclesFromDB,
        loadPaymentTypes,
        loadSettings,
        impersonation,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export type {
  Driver,
  Job,
  FleetVehicle,
  AdminSettings,
  BonusTier,
  TimesheetEntry,
  BreakEntry,
  PaymentType,
  ImpersonationState,
};